'use client'
import { useRef, useState } from 'react'
import Cookies from 'js-cookie'
import { Typography } from 'components/Contentful'
import Section, { SECTION_VARIANTS } from 'components/Section'
import { TitleDivider } from '@/components/TitleDivider'
import { useMemoizedContentGetter } from 'utils/contentful'
import { useDebtAssessment } from 'pageSections/DebtPayoffCalculator/useDebtAssessment'
import { formatDollar } from 'pageSections/DebtPayoffCalculator/utils'
import { useTriggerEvents } from 'hooks/useTriggerEvents'
import { createExperimentObject } from 'utils/experiments'
import { ComparisonCard } from './ComparisonCard'

const LEARN_MORE_LINKS = ['/personal-loans', '/home-equity-loan']
const OVERFLOW_THUMB_WIDTH = 200

const CIRCLE_PATH =
  'M28.0408 15.2952C28.0408 22.3346 22.3342 28.0411 15.2948 28.0411C8.25539 28.0411 2.54883 22.3346 2.54883 15.2952C2.54883 8.25576 8.25539 2.54919 15.2948 2.54919C22.3342 2.54919 28.0408 8.25576 28.0408 15.2952Z'

const StepperIcon = ({ children }) => (
  <svg
    width="30"
    height="30"
    viewBox="0 0 31 31"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d={CIRCLE_PATH}
      stroke="#1D252F"
      strokeWidth="2.54919"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d={children}
      stroke="#1D252F"
      strokeWidth="2.54919"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const MinusCircleIcon = () => <StepperIcon>{'M10.1964 15.2952H20.3932'}</StepperIcon>
const PlusCircleIcon = () => (
  <StepperIcon>
    {
      'M9.94141 15.2222H15.3491M15.3491 15.2222H20.7567M15.3491 15.2222V20.6299M15.3491 15.2222V9.81458'
    }
  </StepperIcon>
)

const StepperButton = ({ label, disabled, onClick, children }) => (
  <button
    type="button"
    aria-label={label}
    disabled={disabled}
    onClick={onClick}
    className="flex box-border! h-[30px]! w-[30px]! shrink-0 cursor-pointer items-center justify-center border-0 bg-transparent p-0! transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
  >
    {children}
  </button>
)

function DebtComparisonCalculatorModule({
  config,
  title,
  eyebrow,
  subTitle,
  sectionVariationObject,
}) {
  const [scrollThumbOffset, setScrollThumbOffset] = useState(0)
  const overflowTrackRef = useRef(null)
  const json = config?.jsonContent || {}
  const sliderConfig = json.slider || {}
  const apiUrl = json.apiUrl || '/api/achieve-tools/debt-assessment'
  const backgroundColor = json.backgroundColor || ''
  const labels = json.labels || {}
  const cards = json.cards || []

  const step = sliderConfig.step || 1000
  const min = sliderConfig.min ?? 0
  const max = sliderConfig.max ?? 100000

  const { selectedValue, results, handleChangeCommitted } = useDebtAssessment(
    sliderConfig,
    apiUrl,
    json.defaultValues
  )
  const { fireAndForget } = useTriggerEvents()

  const clamp = (value) => Math.min(max, Math.max(min, value))
  const handleStep = (delta, clickId) => {
    const nextValue = clamp(selectedValue + delta)
    fireAndForget({
      track_event: 'internal_campaign_click',
      event_action: 'button_click',
      event_type: 'button_click',
      click_id: clickId,
      click_text: `$${formatDollar(nextValue)}`,
      nav_link_section: json.title || 'Debt Comparison Calculator',
      ...(sectionVariationObject?.variant && {
        experiments: [
          createExperimentObject(
            sectionVariationObject.name,
            sectionVariationObject.featureFlagId,
            sectionVariationObject.variant,
            'conversion'
          ),
        ],
      }),
    })
    handleChangeCommitted(null, nextValue)
  }
  const handleCardsScroll = (event) => {
    const carousel = event.currentTarget
    const maxScroll = carousel.scrollWidth - carousel.clientWidth
    const trackWidth = overflowTrackRef.current?.clientWidth ?? carousel.clientWidth
    const maxThumbOffset = trackWidth - OVERFLOW_THUMB_WIDTH
    setScrollThumbOffset(
      maxScroll > 0 && maxThumbOffset > 0
        ? Math.min(maxThumbOffset, (carousel.scrollLeft / maxScroll) * maxThumbOffset)
        : 0
    )
  }

  return (
    <Section
      disableGutters
      variant={json.sectionVariant ?? SECTION_VARIANTS.white}
      backgroundColor={backgroundColor}
      className="mt-12 breakpointMd:mt-20 mb-12 breakpointMd:mb-20 px-5 breakpointMd:px-8"
      childContainerClassName="grid grid-cols-4 breakpointMd:grid-cols-6 breakpointLg:grid-cols-12 gap-x-4 gap-y-0 breakpointMd:gap-x-8"
      data-testid="DebtComparisonCalculator_section"
    >
      <div className="col-span-full min-w-0 -mx-5 breakpointMd:-mx-8 breakpointLg:mx-0 rounded-2xl lg:rounded-[20px] bg-[#F8F9FC] px-5 py-12 lg:px-10 lg:py-14">
        <TitleDivider
          eyebrowBlack={true}
          eyebrow={eyebrow || (json.eyebrow ? { textContent: json.eyebrow } : undefined)}
          title={title || json.title}
          subtitle={subTitle || json.subTitle}
          eyebrowClassName="max-[767px]:text-left!"
          className="mb-4 lg:mb-7"
          classNameTitleText="lg:text-[52px] min-[768px]:max-[1023px]:text-[52px] text-[24px] font-bold max-[767px]:font-ultramarine max-[767px]:text-[32px]! max-[767px]:leading-[130%]! max-[767px]:text-left!           max-[767px]:text-[#1D252F]! max-[767px]:text-wrap!"
          classNameSubtitleText="text-[#42546B] text-[14px] lg:text-[18px] max-[767px]:text-[16px]! max-[767px]:leading-[140%]! max-[767px]:text-left! max-[767px]:text-[#1D252F]! max-[767px]:mt-4! max-[767px]:text-wrap!"
        />

        <div
          className="mb-10 flex items-center justify-between gap-8 md:justify-center!"
          data-testid="DebtComparisonCalculator-stepper"
        >
          <StepperButton
            label={labels.decreaseAria || 'Decrease debt amount'}
            disabled={selectedValue <= min}
            onClick={() => handleStep(-step, labels.decreaseAria || 'Decrease debt amount')}
          >
            <MinusCircleIcon />
          </StepperButton>
          <Typography
            content={`$${formatDollar(selectedValue)}${
              selectedValue >= max && sliderConfig.maxPlus ? '+' : ''
            }`}
            variant="displaySm"
            fontWeight="bold"
            className="text-center text-[32px] font-bold text-[#1D252F] min-[768px]:text-[40px]"
            aria-live="polite"
            aria-atomic="true"
            data-testid="DebtComparisonCalculator-amount"
          />
          <StepperButton
            label={labels.increaseAria || 'Increase debt amount'}
            disabled={selectedValue >= max}
            onClick={() => handleStep(step, labels.increaseAria || 'Increase debt amount')}
          >
            <PlusCircleIcon />
          </StepperButton>
        </div>

        <div
          className="grid grid-flow-col auto-cols-[minmax(181px,181px)] breakpointMd:auto-cols-[minmax(225px,263px)] breakpointMd:justify-center-safe gap-2 -mx-5 px-5 scroll-px-5 overflow-x-auto overflow-y-visible snap-x snap-mandatory [&::-webkit-scrollbar]:hidden breakpointLg:grid-flow-row breakpointLg:auto-cols-auto breakpointLg:grid-cols-[repeat(4,minmax(225px,263px))] breakpointLg:justify-center breakpointLg:gap-4 breakpointLg:mx-0 breakpointLg:px-0 breakpointLg:scroll-px-0 breakpointLg:pb-0"
          data-testid="DebtComparisonCalculator-container"
          style={{ scrollbarWidth: 'none' }}
          onScroll={handleCardsScroll}
          tabIndex={0}
        >
          {cards.map((card, index) => {
            const result = results?.[index] || {}
            const hasData =
              result.active !== false &&
              [result.resultMonthly, result.resultTotal, result.resultMonths].some(
                (value) => Number(value) > 0
              )
            const isDebtRelief = card.key === 'ar'
            let ctaLink = result.ctaLink1 || card.ctaLink
            if (isDebtRelief && card.hasCta && card.ctaLink) {
              const url = new URL(card.ctaLink)
              url.searchParams.set('from', 'achieve')
              url.searchParams.set('estimated_debt', String(selectedValue))
              const visitId = Cookies.get('eh_visit_id')
              if (visitId) {
                url.searchParams.set('visit_id', visitId)
              } else {
                url.searchParams.delete('visit_id')
              }
              ctaLink = url.toString()
            }
            return (
              <div key={card.key || index} className="flex min-w-0 snap-start [&>div]:w-full">
                <ComparisonCard
                  iconSrc={card.icon}
                  iconAlt={card.iconAlt}
                  title={card.title}
                  subheading={
                    index === 2 ? (
                      <>
                        provided by <strong>Freedom Debt Relief</strong>
                      </>
                    ) : null
                  }
                  badgeText={card.badge}
                  badgeVariant={card.badgeVariant}
                  active={card.alwaysActive || result.active}
                  sectionTitle={json.title || 'Debt Comparison Calculator'}
                  hasData={hasData}
                  externalLink={index === 2}
                  unavailableBadgeText={labels.unavailable || 'Unavailable'}
                  learnMore={
                    LEARN_MORE_LINKS[index] || card.learnMoreLink
                      ? {
                          text: labels.learnMore || 'Learn more',
                          link: card.learnMoreLink || LEARN_MORE_LINKS[index],
                        }
                      : null
                  }
                  resultMonthly={result.resultMonthly}
                  resultMonthlyText={labels.monthly}
                  resultTotal={result.resultTotal}
                  resultTotalText={labels.total}
                  resultMonths={result.resultMonths}
                  resultMonthsText={labels.months}
                  monthlySuffix={labels.monthlySuffix}
                  monthsSuffix={labels.monthsSuffix}
                  cta={
                    card.hasCta
                      ? {
                          text: result.ctaText1 || card.ctaText,
                          link: ctaLink,
                          queryParamsToPreserve: isDebtRelief
                            ? ['from', 'estimated_debt', 'visit_id']
                            : undefined,
                          track: result.track1,
                        }
                      : null
                  }
                  sectionVariationObject={sectionVariationObject}
                  pos={index}
                />
              </div>
            )
          })}
        </div>
        <div
          ref={overflowTrackRef}
          className="mx-auto mt-6 h-[3px] w-full max-w-[335px] overflow-hidden rounded-full bg-[#EFF2F8] min-[768px]:mx-3 min-[768px]:w-auto min-[768px]:max-w-none breakpointLg:hidden"
          data-testid="DebtComparisonCalculator-overflow-track"
          aria-hidden="true"
        >
          <div
            className="h-full w-[200px] rounded-full bg-(--content-secondary,#5C708A)"
            data-testid="DebtComparisonCalculator-overflow-thumb"
            style={{ transform: `translateX(${scrollThumbOffset}px)` }}
          />
        </div>

        {json.disclaimer && (
          <Typography
            data-testid="DebtComparisonCalculator-disclaimer"
            className="m-auto max-w-202 pt-8 text-center text-[14px] text-[#42546b] min-[768px]:max-[1023px]:mx-0 min-[768px]:max-[1023px]:max-w-none min-[768px]:max-[1023px]:text-left max-[767px]:max-w-[335px] max-[767px]:text-left! max-[767px]:text-[14px]! max-[767px]:leading-[140%]! max-[767px]:text-[#5C708A]!"
            content={json.disclaimer}
          />
        )}
      </div>
    </Section>
  )
}

function DebtComparisonCalculator({ content }) {
  const {
    jsonContent: config,
    title,
    eyebrow,
    subTitle,
  } = useMemoizedContentGetter(content, ['jsonContent', 'title', 'eyebrow', 'subTitle'])
  const sectionVariationObject = {
    ...content?.fields?.experiment,
    variant: content?.fields?.experimentVariation,
  }
  return (
    <DebtComparisonCalculatorModule
      config={config}
      title={title}
      eyebrow={eyebrow}
      subTitle={subTitle}
      sectionVariationObject={sectionVariationObject}
    />
  )
}

export { DebtComparisonCalculator, DebtComparisonCalculatorModule }
export default DebtComparisonCalculator
